import { createDefaultNativeStackNavigator, createWebModalScreenOptions } from 'app/lib/navigation';
import type { TextMessageMessage } from 'app/types/chat';
import type { Loan } from 'app/types/loan';
import { LoanDatesPickerScreen } from '../shared/loan-dates-picker';
import { AllChannelsScreen } from './all-channels';
import { AddChatMembersScreen } from './add-chat-members';
import { ChatMembersScreen } from './chat-members';
import { ListingBonificationScreen } from './listing-bonification';
import { NewConversationScreen } from './new-conversation';
import { NewGroupChatScreen } from './new-group-chat';
import { OneChannelScreen, OneChannelScreenWithChatLayout } from './one-channel';
import { HowItWorksScreen } from './one-channel/components/how-it-works-card';
import { useUISize } from 'app/utils/web-helpers';
import type { ScreenOptionsNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import type { ComponentProps } from 'react';
import { type Listing } from 'app/types/listing';
import { MessageFooterProvider } from './message-footer-context';

export type Parameters = {
  'all-channels':
    | {
        archiveTab?: boolean;
        archivedConvoChannelUrl?: string;
        url?: string;
        messagePrompt?: TextMessageMessage;
        toast?: string;
        listingId?: Listing['id'];
        userId?: string;
        count?: number;
      }
    | undefined;
  'one-channel': {
    url: string;
    messagePrompt?: TextMessageMessage;
    toast?: string;
    listingId?: Listing['id'];
    userId?: string;
    count?: number;
  };
  'loan-dates-picker': {
    url: string;
    promptIntroduceYourself: boolean;
    goBackTo: 'one-channel' | 'one-loan';
    loanId?: Loan['id'];
  };
  'listing-bonification': {
    loanId?: Loan['id'];
    bonificationId?: string;
    listingId?: Listing['id'];
  };
  'new-conversation': undefined;
  'new-group-chat': undefined;
  'chat-members': { url: string };
  'add-chat-members': { url: string };
  'chat-how-it-works': { isLender: boolean };
};

export const initialRouteName = 'all-channels';

export { linking } from './navigator.linking';

const StackPage = createDefaultNativeStackNavigator<Parameters>({
  isModal: false,
  withChat: false,
  withInvite: false,
});

export function Navigator() {
  const { isSmallUI, isLargeUI } = useUISize();
  const Stack = StackPage;
  const OneChannelComponent = isSmallUI ? OneChannelScreen : OneChannelScreenWithChatLayout;
  const getWebModalOptions = (navigation: ScreenOptionsNavigationProp, route: SpecificRouteProp) => {
    if (isLargeUI) {
      return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
    }

    return {};
  };

  return (
    <MessageFooterProvider>
      <Stack.Navigator initialRouteName={initialRouteName} screenOptions={{ cardStyle: { backgroundColor: 'white' } }}>
        <Stack.Screen component={AllChannelsScreen} name="all-channels" />
        <Stack.Screen component={OneChannelComponent} name="one-channel" />
        <Stack.Screen
          component={LoanDatesPickerScreen}
          name="loan-dates-picker"
          options={({ navigation, route }) => {
            if (isLargeUI) {
              return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
            }

            return {};
          }}
        />
        <Stack.Screen
          component={ListingBonificationScreen}
          name="listing-bonification"
          options={({ navigation, route }) => {
            if (!isSmallUI) {
              return createWebModalScreenOptions(navigation, route, false) as ComponentProps<typeof Stack.Navigator>;
            }

            return {};
          }}
        />
        <Stack.Screen
          component={HowItWorksScreen}
          name="chat-how-it-works"
          options={({ navigation, route }) => {
            if (!isSmallUI) {
              return createWebModalScreenOptions(navigation, route, false) as ComponentProps<typeof Stack.Navigator>;
            }

            return { headerShown: false };
          }}
        />
        <Stack.Screen
          component={NewConversationScreen}
          name="new-conversation"
          options={({ navigation, route }) => getWebModalOptions(navigation, route)}
        />
        <Stack.Screen
          component={NewGroupChatScreen}
          name="new-group-chat"
          options={({ navigation, route }) => getWebModalOptions(navigation, route)}
        />
        <Stack.Screen
          component={ChatMembersScreen}
          name="chat-members"
          options={({ navigation, route }) => getWebModalOptions(navigation, route)}
        />
        <Stack.Screen
          component={AddChatMembersScreen}
          name="add-chat-members"
          options={({ navigation, route }) => getWebModalOptions(navigation, route)}
        />
      </Stack.Navigator>
    </MessageFooterProvider>
  );
}
